<template>
    <v-hover v-slot="{ isHovering: hover }">
        <v-card
            :class="{ 'on-hover': hover, 'pa-6': true }"
            elevation="0"
            :style="{
                'background-color':
                    hover || selectedRecord ? '#e0e0e0' : '#ffffff',
                width: '100%',
            }"
        >
            <v-row style="width: 100%" class="ma-0">
                <v-col cols="9" v-if="record">
                    <div class="text-caption">{{ dateString }}</div>
                    <div class="d-flex my-2">
                        <EWR
                            v-if="output.errors && output.errors.length > 0"
                            icon="mdi-alert-octagon"
                            color="#ff5252"
                            :number="output.errors.length"
                            text="error"
                        />
                        <EWR
                            v-if="output.warnings && output.warnings.length > 0"
                            class="ml-2"
                            icon="mdi-alert"
                            color="#ffc107"
                            :number="output.warnings.length"
                            text="warning"
                        />
                        <EWR
                            v-if="output.remarks && output.remarks.length > 0"
                            class="ml-2"
                            icon="mdi-information"
                            color="#2196f3"
                            :number="output.remarks.length"
                            text="remark"
                        />
                    </div>
                    <v-row style="width: 100%">
                        <v-col sm="2" md="1" class="d-flex align-center"
                            ><b>Inputs</b></v-col
                        >
                        <v-col
                            sm="10"
                            md="11"
                            style="
                                width: 100%;
                                text-overflow: ellipsis;
                                overflow-wrap: anywhere;
                                word-break: break-all;
                                overflow-x: hidden;
                            "
                        >
                            {{ inputsString }}
                        </v-col>
                    </v-row>
                    <v-row style="width: 100%">
                        <v-col sm="2" md="1" class="d-flex align-center"
                            ><b>Outputs</b></v-col
                        >
                        <v-col
                            sm="10"
                            md="11"
                            style="
                                text-overflow: ellipsis;
                                overflow-wrap: anywhere;
                                word-break: break-all;
                                overflow: hidden;
                            "
                        >
                            <div
                                v-katex:auto="{ options: katexOptions }"
                                v-if="record.isSingleResults"
                                v-html="singleResultString"
                            ></div>
                            <div
                                v-else
                                v-katex:auto="{ options: katexOptions }"
                            >
                                {{ resultString }}
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="3" class="d-flex align-center">
                    <RecordButtons
                        :record="record"
                        @restore="restore"
                        @share="() => (shareDialog = true)"
                    />
                </v-col>
                <ShareDialog :record="record" v-model="shareDialog" />
            </v-row>
        </v-card>
    </v-hover>
</template>
<script setup>
import { ref, computed } from 'vue'
import EWR from './EWR.vue'
import ShareDialog from './ShareDialog.vue'
import RecordButtons from './RecordButtons.vue'

const props = defineProps(['record', 'selectedRecord'])
const emit = defineEmits(['restore'])

const shareDialog = ref(false)
const pdfDownload = ref(false)
const katexOptions = ref({
    delimiters: [{ left: '$$', right: '$$', display: false }],
})

function restore() {
    emit('restore', props.record)
}

function stringifyResult(html, units, symbol, value) {
    if (html != null) return html
    else {
        let u = units ? ' ' + units : ''
        return symbol + ' = ' + value + u
    }
}

function stringifyFile(name, url) {
    const pathSplit = new URL(url).pathname.split('/')
    return `${name} = ${pathSplit[pathSplit.length - 1]}`
}

const singleResultString = computed(() => {
    if (props.record.calc.hasFileOutputs && props.record.isSingleFileResults) {
        return stringifyFile('Default', props.record.output.result)
    }
    return props.record.output.result
})

const singleFileResultString = computed(() => {
    if (props.record.calc.hasFileOutputs && props.record.isSingleFileResults) {
        return stringifyFile('Default', props.record.output.result)
    }
    return ''
})

const inputsString = computed(() => {
    let inputs = []
    for (let b in props.record.body) {
        let value = props.record.body[b]
        if (value instanceof File) {
            inputs.push(`${b}=${value.name}`)
        } else {
            inputs.push(`${b}=${JSON.stringify(value).replaceAll('"', '')}`)
        }
    }
    return inputs.join(', ')
})

const dateString = computed(() => new Date(props.record.date).toLocaleString())

const output = computed(() => props.record.output)

const resultString = computed(() => {
    var short = []
    if (
        !props.record.output.arupComputeResultItems &&
        !props.record.output.arupComputeResultFileItems
    )
        return ''
    for (var r in props.record.output.arupComputeResultItems) {
        let results = props.record.output.arupComputeResultItems
        // eslint-disable-next-line no-prototype-builtins
        if (results.hasOwnProperty(r)) {
            short.push(
                stringifyResult(
                    results[r].html,
                    results[r].units,
                    results[r].symbol,
                    results[r].value
                )
            )
        } else {
            short.push(stringifyResult(r.html, r.units, r.symbol, r.value))
        }
    }
    for (let f in props.record.output.arupComputeResultFileItems) {
        let results = props.record.output.arupComputeResultFileItems
        short.push(stringifyFile(results[f].fileName, results[f].fileLink))
    }
    return short.join(', ')
})
</script>
